import { Injectable } from '@angular/core';
import { IUserAssignments } from 'src/app/interfaces/self-serve/assignment-manager/iuser-assignments';
import {
  IParsedTeam,
  IParsedUser,
} from 'src/app/interfaces/self-serve/assignment-manager/iteams-with-users-parser';
import {
  IInputChildTeam,
  IInputTeam,
  IInputUser,
  IUsersWithTeams,
} from 'src/app/interfaces/self-serve/assignment-manager/responses/iusers-with-teams';

@Injectable({
  providedIn: 'root',
})
export class TeamUserParserService {
  unassignedTeamUuid = 'unassigned';
  unassignedUsersTeamName =
    'Users without teams (Must be part of a team before being added to assignment)';

  transformTeamUserData(data: IUsersWithTeams): IParsedTeam[] {
    const userMap = this.groupUsersByTeam(data.users);

    // Create deeper hierarchy by analyzing team relationships
    const hierarchicalTeams = this.createHierarchicalTeams(data.teams);

    const parsedTeams = hierarchicalTeams.map((team) =>
      this.buildTeamRecursive(team, userMap, {})
    );

    // Add unassigned users if any
    if (userMap[this.unassignedTeamUuid]?.length) {
      // Disable all unassigned users
      const disabledUnassignedUsers = userMap[this.unassignedTeamUuid].map(
        (user) => ({
          ...user,
          disabled: true,
        })
      );

      parsedTeams.push({
        uuid: this.unassignedTeamUuid,
        name: this.unassignedUsersTeamName,
        expanded: false,
        users: disabledUnassignedUsers,
        children: [],
      });
    }

    return parsedTeams;
  }

  private createHierarchicalTeams(teams: IInputTeam[]): IInputTeam[] {
    // Build hierarchy by comparing childTeam.uuid with parent.teamUuid recursively
    const processedTeams = new Set<string>();
    const hierarchicalTeams: IInputTeam[] = [];

    for (const team of teams) {
      if (processedTeams.has(team.teamUuid)) continue;

      // Process the team and build its hierarchy recursively
      const processedTeam = this.buildTeamHierarchy(
        team,
        teams,
        processedTeams
      );
      hierarchicalTeams.push(processedTeam);
    }

    return hierarchicalTeams;
  }

  private buildTeamHierarchy(
    team: IInputTeam,
    allTeams: IInputTeam[],
    processedTeams: Set<string>
  ): IInputTeam {
    // Check if this team has already been processed
    const isAlreadyProcessed = processedTeams.has(team.teamUuid);

    if (!isAlreadyProcessed) {
      processedTeams.add(team.teamUuid);
    }

    // Process child teams recursively
    const processedChildTeams: IInputChildTeam[] = [];

    if (team.childTeams && team.childTeams.length > 0) {
      for (const childTeam of team.childTeams) {
        // Find the actual team with teamUuid === childTeam.uuid
        const actualChildTeam = allTeams.find(
          (t) => t.teamUuid === childTeam.uuid
        );

        if (actualChildTeam) {
          // Recursively process this child team
          const processedChild = this.buildTeamHierarchy(
            actualChildTeam,
            allTeams,
            processedTeams
          );

          // Create hierarchical child with its own children
          const hierarchicalChild: IInputChildTeam = {
            uuid: processedChild.teamUuid,
            name: processedChild.teamName,
            reportGroups: null,
            managers: null,
            memberCount: 0,
            childTeams: processedChild.childTeams || [],
          };

          processedChildTeams.push(hierarchicalChild);
        } else {
          // If no actual team found, add the child as is
          processedChildTeams.push(childTeam);
        }
      }
    }

    return {
      ...team,
      childTeams: processedChildTeams,
    };
  }

  private groupUsersByTeam(users: IInputUser[]): Record<string, IParsedUser[]> {
    const map: Record<string, IParsedUser[]> = {};

    for (const user of users) {
      const teamId = user.teamUuid ?? this.unassignedTeamUuid;
      const teamName = user.teamName ?? this.unassignedUsersTeamName;

      const parsedUser: IParsedUser = {
        uuid: user.userUuid,
        teamUuid: teamId,
        teamName: teamName,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        selected: false,
        disabled: false,
      };

      if (!map[teamId]) {
        map[teamId] = [];
      }

      map[teamId].push(parsedUser);
    }

    return map;
  }

  private buildTeamRecursive(
    team: IInputTeam | IInputChildTeam,
    userMap: Record<string, IParsedUser[]>,
    teamIndex: Record<string, IInputChildTeam>
  ): IParsedTeam {
    const teamUuid = 'teamUuid' in team ? team.teamUuid : team.uuid;
    const teamName = 'teamName' in team ? team.teamName : team.name;

    // Get child teams - handle both IInputTeam and IInputChildTeam
    let childrenInput: IInputChildTeam[] = [];
    if ('teamUuid' in team) {
      // This is an IInputTeam
      childrenInput = (team as IInputTeam).childTeams ?? [];
    } else {
      // This is an IInputChildTeam
      childrenInput = (team as IInputChildTeam).childTeams ?? [];
    }

    const children: IParsedTeam[] = childrenInput.map((child) => {
      // Use the indexed version if available, otherwise use the original child
      const indexedChild = teamIndex[child.uuid];
      return this.buildTeamRecursive(indexedChild || child, userMap, teamIndex);
    });

    return {
      uuid: teamUuid,
      name: teamName,
      expanded: false,
      users: userMap[teamUuid] ?? [],
      children,
    };
  }

  markAssignedUsersOnParsedTeams(
    parsedTeams: IParsedTeam[],
    currentAssignees: IUserAssignments[]
  ): IParsedTeam[] {
    if (currentAssignees.length === 0) return parsedTeams;

    const assignmentSet = new Set(
      currentAssignees.map((a) => `${a.userUuid}__${a.teamUuid}`)
    );

    const markRecursive = (teams: IParsedTeam[]): IParsedTeam[] => {
      return teams.map((team) => {
        const updatedUsers = team.users.map((user) => {
          const isAssigned = assignmentSet.has(
            `${user.uuid}__${user.teamUuid}`
          );
          // Disable users if they are in unassigned team or if they are already assigned
          const shouldDisable =
            isAssigned || user.teamUuid === this.unassignedTeamUuid;
          return {
            ...user,
            selected: isAssigned,
            disabled: shouldDisable,
          };
        });

        const updatedChildren = markRecursive(team.children || []);
        return {
          ...team,
          users: updatedUsers,
          children: updatedChildren,
        };
      });
    };

    return markRecursive(parsedTeams);
  }
}
